import { ApiError } from '@/lib/api-client';

/** What the restore page needs to render a failure. */
export interface RestoreErrorCopy {
  message: string;
  /** Whether the "request a fresh link" advice is appended to `message`. */
  retryable: boolean;
}

/**
 * The string the page shows when nothing better is available.
 *
 * Note it is NOT the copy for an expired link, despite reading like it. The
 * real expired case is a 410 with its own wording below; a story that asserted
 * this string as "the expired case" was asserting the fallback.
 */
export const RESTORE_FALLBACK_MESSAGE =
  'This restore link is invalid or expired.';

/** Reached with no `?token=` at all — handled before any request is made. */
export const RESTORE_MISSING_TOKEN: RestoreErrorCopy = {
  message: 'This restore link is missing its token.',
  retryable: true,
};

/**
 * Status-specific copy, because the generic fallback leaked HTTP into the page.
 *
 * The page's catch used to read `detail || message || <generic>`. Two of the
 * endpoint's responses carry neither field: the 429 from
 * `@limiter.limit("10/minute")` goes through slowapi's default handler, which
 * returns `{"error": ...}`, and a transport failure builds `ApiError(0, ...)`
 * from the fetch rejection. `apiClient` then falls through to
 * `HTTP <status>: <statusText>` and the page printed that string to the user.
 *
 * `retryable` is the other half, and the reason this is a table rather than a
 * message lookup. The body used to append "If you are still inside the 30-day
 * window, request a fresh link from the sign-in screen" unconditionally - which
 * is wrong for precisely the two cases where nothing is left to restore: 410
 * (window closed, data permanently deleted) and 404 (the row is gone). Sending
 * someone back for another link there is a loop with no exit.
 *
 * 404 deliberately has no `message`: the backend's own "Account no longer
 * exists" is already the clearest wording, and only its advice was wrong.
 */
export const RESTORE_ERROR_COPY: Partial<
  Record<number, { message?: string; retryable: boolean }>
> = {
  0: {
    message:
      'We could not reach the server. Check your connection and open the link again.',
    retryable: true,
  },
  404: { retryable: false },
  410: {
    message:
      'The 30-day recovery window has closed and this account has been permanently deleted.',
    retryable: false,
  },
  429: {
    message: 'Too many attempts. Please wait a minute and open the link again.',
    retryable: true,
  },
};

/**
 * Turn whatever `accountRecoveryApi.restore` threw into copy the page can show.
 *
 * Extracted from `AccountRestorePage` so the branching is testable as the pure
 * lookup it is. It used to be exercised by six near-identical Storybook stories
 * that rendered one error card and differed only in the string inside it - real
 * coverage of a table, paid for in snapshots.
 *
 * Precedence, unchanged: status override -> the backend's `detail` -> the
 * error's `message` -> the fallback.
 */
export function resolveRestoreError(err: unknown): RestoreErrorCopy {
  // `-1` rather than 0 for a non-ApiError throw: 0 is the real status of a
  // transport failure and has its own entry above.
  const override =
    RESTORE_ERROR_COPY[err instanceof ApiError ? err.status : -1];

  const message =
    override?.message ||
    (err as { detail?: string } | null)?.detail ||
    (err as { message?: string } | null)?.message ||
    RESTORE_FALLBACK_MESSAGE;

  return { message, retryable: override?.retryable ?? true };
}

/**
 * The same table, for the *request-a-link* endpoint rather than the link itself.
 *
 * Shares `RESTORE_ERROR_COPY`'s 0 and 429 entries - a dead connection and the
 * backend's `5/minute` limit mean the same thing on either call - but NOT its
 * fallback. `RESTORE_FALLBACK_MESSAGE` talks about a link being invalid or
 * expired, and on this endpoint no link exists yet: the user is asking for one.
 *
 * The backend deliberately returns `{ sent: true }` even for an address it does
 * not recognise, to prevent account enumeration, so a failure here really is a
 * failure to send rather than anything about the account.
 */
export const REQUEST_RESTORE_FALLBACK =
  'Failed to send recovery link. Please try again.';

export function resolveRequestRestoreError(err: unknown): string {
  const status = err instanceof ApiError ? err.status : -1;
  return RESTORE_ERROR_COPY[status]?.message ?? REQUEST_RESTORE_FALLBACK;
}
